import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const goodSolutionPoints = [
  { title: 'Domain Expertise', desc: 'Generic LLMs lack professional-grade knowledge. Effective GenAI solutions are built with domain-specific context, standards, and institutional memory baked in.' },
  { title: 'Enterprise Governance', desc: 'Production GenAI requires role-based access, audit trails, approval gates, and policy-as-code controls — not just a chat interface on top of an API.' },
  { title: 'Human-in-the-Loop', desc: 'The best GenAI solutions amplify human judgment, not replace it. Expert steering and escalation paths must be designed into the workflow from the start.' },
];

const agents = [
  { label: 'Cloud Architect Agent', path: '/agents/cloud-architect', desc: 'Cloud architecture, IaC, and Well-Architected review.' },
  { label: 'CPA Agent', path: '/agents/cpa', desc: 'IFRS/GAAP accounting, reconciliations, and financial reporting.' },
  { label: 'Data Engineer Agent', path: '/agents/data-engineering', desc: 'Pipeline design, schema governance, and data platform builds.' },
  { label: 'Recruiter Agent', path: '/agents/recruiter-agent', desc: 'Candidate screening, shortlisting, and recruiter workflow automation.' },
  { label: 'Interview Coach', path: '/agents/interview-agent', desc: 'Interview question generation, scoring, and candidate feedback.' },
  { label: 'SDR Agent', path: '/agents/sdr-agent', desc: 'Sales prospecting, outreach automation, and pipeline building.' },
  { label: 'KYC Onboarding Agent', path: '/agents/kyc-onboarding', desc: 'Identity verification and KYC workflow automation.' },
  { label: 'Governance Audit Agent', path: '/agents/governance-audit', desc: 'Policy-as-code compliance and governance automation.' },
];

const implementationSteps = [
  { step: 'Assess', desc: 'Identify the highest-value workflows, evaluate AI readiness, and select the right agents and models for your use case.' },
  { step: 'Deploy', desc: 'Configure and connect agents to your enterprise systems, data sources, and approval workflows with minimal integration overhead.' },
  { step: 'Govern', desc: 'Set up audit trails, approval gates, role-based access controls, and policy-as-code guardrails for safe production operation.' },
  { step: 'Scale', desc: 'Expand automation across teams and functions, measure ROI, and continuously improve agent performance with feedback loops.' },
];

const faqs = [
  {
    q: 'What is a GenAI solution for enterprise?',
    a: 'A GenAI solution for enterprise is a production-grade system that uses large language models (LLMs) to automate, augment, or accelerate specific business workflows with governance, security, and human oversight built in.',
  },
  {
    q: 'How does Eagentix help enterprises build GenAI solutions?',
    a: 'Eagentix provides pre-built agentic accelerators for specific domains (cloud, finance, data, HR, sales) so enterprises can deploy production-ready GenAI without building from scratch.',
  },
  {
    q: 'What models does Eagentix use for GenAI solutions?',
    a: 'Eagentix is model-agnostic. Enterprises choose from OpenAI GPT, Anthropic Claude, Mistral, Azure AI, or any supported frontier model based on cost, compliance, or capability requirements.',
  },
  {
    q: 'How long does it take to implement a GenAI solution with Eagentix?',
    a: 'Pre-built Eagentix agents can be deployed within days. Custom enterprise integrations typically take 2–6 weeks depending on scope and infrastructure requirements.',
  },
  {
    q: 'Can GenAI solutions be deployed privately with Eagentix?',
    a: 'Yes. All Eagentix GenAI solutions support private cloud and on-premises deployment, ensuring enterprise data never leaves the customer\'s environment.',
  },
];

export default function BuildGenAISolutions() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="Build Generative AI Solutions for Enterprise | Eagentix"
        description="Eagentix accelerates enterprise GenAI adoption with pre-built, governed agentic accelerators for cloud, finance, data, sales, HR, and operations teams."
        pathname="/use-cases/build-genai-solutions"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Use Case · GenAI Implementation
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Build Generative AI Solutions for Enterprise
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Eagentix accelerates enterprise GenAI adoption with purpose-built agentic accelerators — pre-built, governed, and production-ready for cloud, finance, data, sales, HR, and operations teams.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What makes a good enterprise GenAI solution */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            What Makes a Good Enterprise GenAI Solution
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Three non-negotiable principles that separate production-ready GenAI from demos that don't scale.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {goodSolutionPoints.map((point, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{point.title}</h3>
                <p style={{ color: '#475569', lineHeight: 1.75, margin: 0 }}>{point.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eagentix GenAI accelerators */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Eagentix GenAI Accelerators
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Pre-built agents for the highest-value enterprise use cases — deployable in days, not months.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            {agents.map((agent, i) => (
              <Link
                key={i}
                to={agent.path}
                style={{ ...card, textDecoration: 'none', display: 'block' }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0071e3', marginBottom: '8px' }}>{agent.label}</h3>
                <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.65, margin: 0 }}>{agent.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation approach */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Implementation Approach
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix follows a four-phase approach to GenAI implementation for enterprise.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            {implementationSteps.map((step, i) => (
              <div key={i} style={card}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0071e3', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px' }}>
                  Phase {i + 1}
                </div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '10px' }}>{step.step}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Start Building Enterprise GenAI Today
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Skip months of build time. Deploy pre-built Eagentix GenAI accelerators in your domain within days.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
